import { CdkMenuModule } from "@angular/cdk/menu";
import { CommonModule } from "@angular/common";
import {
    AfterViewChecked,
    Component,
    computed,
    HostBinding,
    OnDestroy,
    signal,
} from "@angular/core";
import dayjs from "dayjs";
import { DndDraggableDirective } from "ngx-drag-drop";
import { injectResize } from "ngxtension/resize";
import { Subject, takeUntil } from "rxjs";
import { Menu } from "../../../menu/menu.component";
import { MenuOption } from "../../../menu/option/menu-option.component";
import { CommonEventAdapter } from "../../adapters/event.adapter";
import { TIMELINE_CALENDAR_DEFAULT_TOP } from "../../calendar.types";
import { CalendarRepeatIcon } from "../../etc/repeat-icon/repeat-icon.component";

@Component({
    selector: "app-all-day-event",
    standalone: true,
    templateUrl: "./all-day-event.component.html",
    styleUrls: ["./all-day-event.component.scss"],
    imports: [
        DndDraggableDirective,
        Menu,
        MenuOption,
        CdkMenuModule,
        CommonModule,
        CalendarRepeatIcon,
    ],
})
export class AllDayTimelineEvent
    extends CommonEventAdapter
    implements AfterViewChecked, OnDestroy
{
    @HostBinding("class") class = "absolute z-10 px-2";
    @HostBinding("style.top") get getTop() {
        return `${this.top() || TIMELINE_CALENDAR_DEFAULT_TOP}px`;
    }
    @HostBinding("style.left") get getLeft() {
        return `${this.left()}px`;
    }
    @HostBinding("style.width") get getWidth() {
        return `${this.width()}px`;
    }

    private destroy$ = new Subject<void>();
    resize$ = injectResize();

    top = signal(0);
    left = signal(0);
    width = signal(0);

    // 이번달부터 시작하는 이벤트가 아닐 경우
    isNotStartCurrentMonth = computed(
        () =>
            dayjs(this.event().startDate).format("MM") !==
            dayjs(this.store.value()).format("MM"),
    );

    isCanceled = computed(
        () => this.event()?.metadata?.enrollmentSchedule?.status === "CANCELED",
    );

    ngAfterViewChecked(): void {
        // 이벤트의 width 설정
        this.handleWidth();

        // left 값 설정
        this.handleLeft();

        // 이벤트의 top 값 설정
        this.handleTop();

        // resize 이벤트 발생 시 width 재설정
        this.resize$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.handleWidth();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleLeftResize(ev: DragEvent): void {
        this.handleResize(ev, "startDate");
    }

    handleRightResize(ev: DragEvent): void {
        this.handleResize(ev, "endDate");
    }

    private handleResize(
        ev: DragEvent,
        dateType: "startDate" | "endDate",
    ): void {
        if (!this.options()?.event?.resize) return;

        const { offsetX } = ev;
        const index = Math.round(offsetX / 128);
        const event = this.event();
        const currentDate = dayjs(event[dateType]);
        let newDate;

        if (index > 0) {
            newDate = currentDate.add(index, "day");
        } else {
            newDate = currentDate.subtract(index * -1, "day");
        }

        if (newDate.format("YYYY-MM") !== currentDate.format("YYYY-MM")) {
            event[dateType] = currentDate.startOf("day").toDate();
        } else {
            event[dateType] = newDate.toDate();
        }
    }

    private handleTop(): void {
        const top = TIMELINE_CALENDAR_DEFAULT_TOP;
        this.top.set(top + this.index() * 32 + this.index() * 8);
    }

    private handleLeft(): void {
        const startDate = dayjs(this.event().startDate);
        const endDate = dayjs(this.event().endDate);

        const dayWidth = this.getDayWidth();
        if (!dayWidth) {
            requestAnimationFrame(() => this.handleLeft());
            return;
        }

        // day mode: always first column offset
        if (this.store.mode() === "day") {
            this.left.set(128);
            return;
        }

        // week mode
        if (this.store.mode() === "week") {
            const weekStart = dayjs(this.store.value()).startOf("week");
            const weekEnd = weekStart.endOf("week");

            // 가시 범위 (week 내 clip)
            const visibleStart = startDate.isBefore(weekStart)
                ? weekStart
                : startDate;
            const visibleEnd = endDate.isAfter(weekEnd) ? weekEnd : endDate;

            const startIndex = visibleStart
                .startOf("day")
                .diff(weekStart.startOf("day"), "day"); // 0..6

            const baseLeft = 128 + startIndex * dayWidth;
            this.left.set(baseLeft);
            return;
        }

        // month mode (기존)
        const startDay = startDate.format("DD");

        // 이번달 시작이 아니면 첫 칸으로
        if (
            dayjs(this.event().startDate).format("MM") !==
            dayjs(this.store.value()).format("MM")
        ) {
            this.left.set(128);
            return;
        }

        const dayElement = document.querySelector(
            `.day[data-day='${startDay}']`,
        );
        const actualWidth =
            (dayElement as HTMLElement)?.clientWidth || dayWidth;
        const daysDifference = Number(startDay);
        const leftPosition = 128 + (daysDifference - 1) * actualWidth;
        this.left.set(leftPosition);
    }

    private handleWidth(): void {
        const startDate = dayjs(this.event().startDate);
        const endDate = dayjs(this.event().endDate);
        const dayWidth = this.getDayWidth();
        if (!dayWidth) {
            requestAnimationFrame(() => this.handleWidth());
            return;
        }

        if (this.store.mode() === "day") {
            this.width.set(dayWidth);
            return;
        }

        if (this.store.mode() === "week") {
            const weekStart = dayjs(this.store.value()).startOf("week");
            const weekEnd = weekStart.endOf("week");

            const visibleStart = startDate.isBefore(weekStart)
                ? weekStart
                : startDate;
            const visibleEnd = endDate.isAfter(weekEnd) ? weekEnd : endDate;

            const span =
                visibleEnd
                    .startOf("day")
                    .diff(visibleStart.startOf("day"), "day") + 1;
            this.width.set(dayWidth * span);
            return;
        }

        // month mode (기존 로직 보강)
        if (
            dayjs(this.event().startDate).format("MM") !==
            dayjs(this.store.value()).format("MM")
        ) {
            // 이전달 시작 → 이번달 1일부터 endDate까지
            const width = dayWidth * Number(dayjs(endDate).format("DD"));
            this.width.set(width);
            return;
        }

        if (startDate.isSame(endDate, "day")) {
            this.width.set(dayWidth);
            return;
        }

        const diff =
            endDate.startOf("day").diff(startDate.startOf("day"), "day") + 1;
        this.width.set(dayWidth * diff);
    }

    private getDayWidth(): number {
        // 우선 실제 day 셀
        const dayEl =
            (document.querySelector(".day-item") as HTMLElement | null) ||
            (document.querySelector(".day[data-day]") as HTMLElement | null);
        return dayEl?.clientWidth || 0;
    }
}
